/**
 * AVA Design System — Modal
 *
 * Modal responsive con bottom-sheet behavior en mobile:
 *   - Overlay con backdrop semitransparente
 *   - Sticky header (icon + title + subtitle + close)
 *   - Body scrollable
 *   - Sticky footer con safe-area-inset-bottom para iOS
 *   - Border-radius solo top (en desktop también full radius)
 *
 * Diferencia con shadcn Dialog: este Modal está diseñado para flujos largos
 * (registrar empresa, nueva solicitud, captura biométrica) donde es importante
 * el bottom-sheet pattern en mobile. Para confirmaciones simples seguir
 * usando shadcn Dialog/AlertDialog.
 *
 * Cierre: click en overlay, click en X, o Escape. Body scroll lock automático.
 */
import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";

export type ModalSize = "sm" | "md" | "lg";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Si true, click en overlay NO cierra (modal modal). Default false. */
  preventOverlayClose?: boolean;
}

const SIZE_MAX_WIDTH: Record<ModalSize, number> = {
  sm: 420,
  md: 560,
  lg: 720,
};

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  size = "md",
  preventOverlayClose = false,
}: ModalProps) {
  // Body scroll lock + Escape para cerrar
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ava-modal-title"
      onClick={preventOverlayClose ? undefined : onClose}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(15, 23, 42, 0.55)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl"
        style={{
          maxWidth: SIZE_MAX_WIDTH[size],
          maxHeight: "92vh",
          boxShadow: "0 -10px 40px rgba(15, 23, 42, 0.18)",
        }}
      >
        {/* Sticky header */}
        <div
          className="sticky top-0 z-10 bg-white flex items-start gap-3 px-5 py-4 border-b"
          style={{ borderColor: "var(--ava-border)" }}
        >
          {icon && (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: "var(--ava-info-50)",
                color: "var(--primary)",
              }}
            >
              {icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2
              id="ava-modal-title"
              className="m-0 text-[17px] font-semibold tracking-tight text-foreground"
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1 leading-snug">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {/* Sticky footer con safe-area-inset-bottom para iOS */}
        {footer && (
          <div
            className="sticky bottom-0 bg-white flex gap-2 justify-end px-4 py-4 border-t"
            style={{
              borderColor: "var(--ava-border)",
              paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
